import { ReactNode, createContext, useContext } from 'react';
import contactContent from '@shared/content/contactContent.json';
import homeContent from '@shared/content/homeContent.json';
import servicesContent from '@shared/content/servicesContent.json';
import siteMeta from '@shared/content/siteMeta.json';
import trainerContent from '@shared/content/trainerContent.json';
import type { SiteContent, Testimonial } from '@shared/siteContent';

interface SiteContentState {
	content: SiteContent | null;
	error: boolean;
	retry: () => void;
}

const SiteContentContext = createContext<SiteContentState | null>(null);

interface StoredTestimonial extends Testimonial {
	published?: boolean;
	submittedAt?: string;
}

// Each visitor reference is its own file (a Decap folder collection). Show only
// the ones an admin has flipped to `published`, newest first.
const testimonialFiles = import.meta.glob<{ default: StoredTestimonial }>(
	'../../../shared/content/testimonials/*.json',
	{ eager: true }
);

const publishedTestimonials: Testimonial[] = Object.values(testimonialFiles)
	.map(mod => mod.default)
	.filter(t => t.published)
	.sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''))
	.map(({ quote, name, rating }) => ({ quote, name, rating }));

// Editors paste copy into /admin from Docs, Word, other pages - a stray leading
// or trailing space rides along and would then render verbatim in titles, the
// footer, SEO tags, etc. Trim every string as the content is assembled so the
// CMS stays forgiving about whitespace.
const deepTrim = <T,>(value: T): T => {
	if (typeof value === 'string') return value.trim() as T;
	if (Array.isArray(value)) return value.map(deepTrim) as T;
	if (value !== null && typeof value === 'object') {
		return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, deepTrim(val)])) as T;
	}
	return value;
};

export const defaultSiteContent: SiteContent = deepTrim({
	siteMeta,
	homeContent,
	servicesContent,
	trainerContent,
	contactContent,
	testimonials: publishedTestimonials,
});

const noop = () => {};

export const SiteContentProvider = ({
	children,
	content = defaultSiteContent,
}: {
	children: ReactNode;
	content?: SiteContent;
}) => (
	<SiteContentContext.Provider value={{ content, error: false, retry: noop }}>{children}</SiteContentContext.Provider>
);

export const useSiteContentState = (): SiteContentState => {
	const ctx = useContext(SiteContentContext);
	if (!ctx) {
		throw new Error('useSiteContentState() must be used within a SiteContentProvider');
	}
	return ctx;
};

export const useSiteContent = (): SiteContent => {
	const { content } = useSiteContentState();
	if (!content) {
		throw new Error('useSiteContent() called before content finished loading');
	}
	return content;
};
